import type { ReactNode } from "react";
import { XIcon } from "lucide-react";
import { Button } from "@component/button";
import { cn } from "@util/tailwind";

// Shared floating "bulk action" pill shown when rows are multi-selected in a
// table. This is the presentational shell only — the count label, the clear-X,
// and the divider chrome. Callers pass their own action buttons as children.
//
// Extracted from the Recruiter applicants bulk bar so every product (Recruiter,
// Trainer/AIQM, …) uses one identical bar instead of re-styling their own.
export default function BulkActionBar({
    label,
    onClear,
    children,
    className,
}: {
    // Full selection label, e.g. "20 calls selected" / "3 candidates selected".
    label: string;
    onClear: () => void;
    // Action buttons/popovers rendered to the right of the divider.
    children?: ReactNode;
    className?: string;
}) {
    return (
        <div
            className={cn(
                "fixed bottom-8 left-1/2 z-40 flex -translate-x-1/2 transform items-center gap-3 rounded-full border border-gray-200 bg-gray-50 px-8 py-4 shadow-lg dark:border-gray-700 dark:bg-gray-900/90",
                className,
            )}
        >
            <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {label}
                </span>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClear}
                    aria-label="Clear selection"
                    className="h-6 w-6 rounded-md p-0 text-gray-500 transition-all hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                    <XIcon className="h-3.5 w-3.5" />
                </Button>
            </div>
            {children && (
                <>
                    <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
                    <div className="flex items-center gap-2">{children}</div>
                </>
            )}
        </div>
    );
}
